export type ToastLevel = 'error' | 'info' | 'success';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  level: ToastLevel;
  message: string;
  detail: string | null;
  action: ToastAction | null;
}

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let counter = 0;

const emit = (): void => {
  listeners.forEach(l => l(toasts));
};

export const subscribeToasts = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(toasts);
  return () => {
    listeners.delete(listener);
  };
};

export const dismissToast = (id: string): void => {
  toasts = toasts.filter(t => t.id !== id);
  emit();
};

export const showToast = (
  message: string,
  opts: {
    level?: ToastLevel;
    detail?: string | null;
    ttlMs?: number | null;
    action?: ToastAction | null;
  } = {},
): string => {
  const id = `t${Date.now()}-${++counter}`;
  const toast: Toast = {
    id,
    level: opts.level ?? 'info',
    message,
    detail: opts.detail ?? null,
    action: opts.action ?? null,
  };
  toasts = [...toasts, toast];
  emit();
  const ttl = opts.ttlMs === undefined ? 6000 : opts.ttlMs;
  if (ttl !== null && ttl > 0) {
    setTimeout(() => dismissToast(id), ttl);
  }
  return id;
};

export const showError = (fallback = 'Something went wrong'): string => {
  return showToast(fallback, { level: 'error', ttlMs: 8000 });
};
